import axios from 'axios';
import { apolloClient } from '@/apollo';
import { IRecipient } from '@lib-shared/types/Recipient';
import { ERecipientRole } from '@lib-shared/recipients/types/enums/recipient-role';
import { RecipientQueries } from '@/api/recipients/graphql/recipient.queries';
import { RecipientMutations } from '@/api/recipients/graphql/recipient.mutations';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';
import { RecipientHelper } from '@/recipients/recipient.helper';
import { IRecipientURL } from '@/api/recipients/types/recipient-url';

const recipientIdFormName = 'recipient-id';
const documentIdFormName = 'document-id';

export class RecipientService {
  static async validateContact(mobile: string) {
    const body = new FormData();
    body.append('mobile', escape(mobile));
    const { data } = await axios.post('/get/contact/validate', body);
    return data || {};
  }

  static async saveRecipientMobile(recipientId: string, documentId: string, mobile: string) {
    const body = new FormData();
    body.append('action', 'mobile');
    body.append('document-id', documentId);
    body.append('recipient-id', recipientId);
    body.append('mobile', mobile);
    const { data } = await axios.post('/save/recipient', body);
    return data || {};
  }

  static async searchRecipient(query: string, documentId: string) {
    let input: string = query;
    if (RecipientHelper.isOnlyNumbers(query)) {
      input = input.charAt(0) === '0' ? input.substring(1) : input;
    }
    const { data }: { data: any } = await axios.get(
      `/get/contact/search?query=${input}&document-id=${documentId}`
    );
    if (!data?.result) {
      return [];
    }
    const searchResult: IRecipient[] = [];

    data.result.forEach(searchItem => {
      const recipient: IRecipient = {
        companyNumber: searchItem.company_id,
        companyName: searchItem.company_name,
        contactId: searchItem.contact_id,
        email: searchItem.email,
        firstName: searchItem.first_name,
        lastName: searchItem.last_name,
        thumbUrl: searchItem.thumb_url,
        mobile: searchItem.mobile,
      };

      searchResult.push(recipient);
    });
    return searchResult;
  }

  static async getRoleDetails(recipientId: string) {
    const { data }: { data: any } = await axios.get(`/get/recipient/${recipientId}/details`);
    const recipientData = data?.result[0];
    if (!recipientData) {
      return;
    }
    return {
      documentId: recipientData.document_id,
      id: recipientData.recipient_id,
      role: recipientData.role,
      roleName: recipientData.role_name,
      verifyEid: recipientData.verify_eid === '1',
      verifySms: recipientData.verify_sms === '1',
      verifySmsOpen: recipientData.verify_sms_open === '1',
      verifySmsSign: recipientData.verify_sms_sign === '1',
    };
  }

  static async saveRecipient(recipient: IRecipient, documentId: string): Promise<string> {
    const body: FormData = new FormData();
    body.append('action', 'save');
    body.append(recipientIdFormName, recipient.id || '');
    body.append('recipient-first_name', recipient.firstName);
    body.append('recipient-last_name', recipient.lastName || '');
    body.append('recipient-role', recipient.role);
    body.append('recipient-email', recipient.email || '');
    body.append('recipient-company_name', recipient.companyName || '');
    body.append('recipient-company_number', recipient.companyNumber || '');
    body.append('recipient-title', recipient.title || '');
    body.append('recipient-mobile', recipient.mobile || '');
    body.append('recipient-note', recipient.note || '');
    body.append('recipient-thumb_url', recipient.thumbUrl || '');
    body.append('verify-eid', (+(recipient.verifyEid || '0')).toString());
    body.append('verify-eid-type', 'bankid');
    body.append(documentIdFormName, documentId);

    const { data } = await axios.post(`/save/recipient`, body);
    if (data.status === 1 || data.status === 2) {
      return data.recipient_id;
    }
    throw new Error(data.error);
  }

  static async createRecipient(
    contactId: string,
    documentId: string,
    role: ERecipientRole
  ): Promise<IRecipient> {
    const { data } = await apolloClient.mutate({
      mutation: RecipientMutations.createRecipientMutation,
      variables: { documentId, contactId, role },
    });
    return data.createRecipient;
  }

  static async addRecipient(payload: IRecipient): Promise<string> {
    const body: FormData = new FormData();
    body.append('action', 'save');
    body.append('recipient-role_name', payload.roleName);
    body.append('recipient-role', payload.role);
    body.append(documentIdFormName, payload.documentId);
    body.append('verify-eid-type', 'bankid');
    body.append('verify-eid', payload.verifyEid.toString());
    body.append('verify-sms', payload.verifySms.toString());
    body.append('verify-sms-open', payload.verifySmsOpen.toString());
    body.append('verify-sms-sign', payload.verifySmsSign.toString());
    if (payload.recipientId) {
      body.append(recipientIdFormName, payload.recipientId);
    }

    const { data } = await axios.post('/save/recipient', body);
    if (data.status === 1 || data.status === 2) {
      return data.recipient_id;
    }
    throw new Error(data.error);
  }

  static async deleteRecipient(id: string): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: RecipientMutations.deleteRecipientMutation,
      variables: { id },
    });
    return !!data;
  }

  static async updateRecipient(id: string, { verifyEidType, ...rest }: Partial<IRecipient>) {
    //temp fix to circumevent graphql enum validation until legacy and graphql endpoints are cleaned up
    if (verifyEidType === '') {
      verifyEidType = 'unknown';
    }

    const {
      data: { updateRecipient },
    } = await apolloClient.mutate({
      mutation: RecipientMutations.updateRecipientMutation,
      variables: { id, ...rest, verifyEidType },
    });
    return updateRecipient;
  }

  static async updateRecipientThumbnail(file: File, email: string): Promise<string> {
    const body: FormData = new FormData();
    body.append('file', file);
    body.append('email', email || '');

    const { data } = await axios.post(`/save/recipient`, body);
    if (data.success) {
      const newThumbnailTimestamp = Math.round(new Date().getTime() / 1000) - 1;
      return `${data.image_url}?${newThumbnailTimestamp}`;
    }
    throw new Error(data.error);
  }

  static async loadRecipients(documentId: string): Promise<IRecipient[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: RecipientQueries.recipientsQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document.recipients;
  }

  static async updateRecipientOrder(documentId: string, recipientOrder: Partial<IRecipient>[]) {
    const { data } = await apolloClient.mutate({
      mutation: RecipientMutations.updateRecipientOrderMutation,
      variables: {
        documentId,
        recipientOrder,
      },
    });
    return data;
  }

  static async assignFieldToRecipient(documentId: string, recipientId: string): Promise<void> {
    const body: FormData = new FormData();
    body.append('action', 'assign-fields');
    body.append(documentIdFormName, documentId);
    body.append(recipientIdFormName, recipientId);
    await axios.post('/save/document', body);
  }

  static async loadRecipientSigningPageURLs(documentId: string): Promise<IRecipientURL[]> {
    const {
      data: { recipientSigningPageUrls },
    } = await apolloClient.query({
      query: RecipientQueries.recipientSigningPageURLs,
      variables: {
        documentId,
      },
    });
    return recipientSigningPageUrls;
  }
}
